// <speechify-agent> custom element.
//
// Drop-in usage:
//   <script src="https://cdn.speechify.ai/widget/agents.js"></script>
//   <speechify-agent agent-id="a_01HS..."></speechify-agent>
//
// Private-agent usage (lazy — preferred, session minted on click):
//   const el = document.querySelector('speechify-agent');
//   el.sessionProvider = async () => {
//     const res = await fetch('/my-backend/create-session');
//     return await res.json(); // { token, url }
//   };
//
// Private-agent usage (eager — legacy, session minted at render):
//   <speechify-agent
//     session-token="…"
//     session-url="wss://…">
//   </speechify-agent>
//
// Per-call overrides (one embed, many segments):
//   <speechify-agent
//     agent-id="a_01HS..."
//     override-prompt="You are a support bot for Acme."
//     override-first-message="Hi — how can I help?"
//     override-voice-id="v_123"
//     override-language="en-US"
//     dynamic-variables='{"customer_tier":"pro"}'
//     avatar-image-url="https://cdn.example.com/avatar.png"
//     avatar-orb-color-1="#6366f1"
//     avatar-orb-color-2="#ec4899"
//     start-call-text="Talk to support"
//     end-call-text="End call"
//     listening-text="Listening…"
//     thinking-text="Thinking…"
//     speaking-text="Speaking…">
//   </speechify-agent>
//
// During local integration, surface integration warnings + verbose
// realtime-client logs by adding the `debug` attribute. Default is
// silent so production embeds don't leak logs into customer devtools.
//   <speechify-agent agent-id="..." debug></speechify-agent>
//
// This file is the element: lifecycle, the call it drives, and the render
// that reflects that call's state. Everything it needs that is not one of
// those three lives next door and is wired in here —
//
//   styles.ts         the stylesheet, the icons, the copy defaults
//   attributes.ts     reading attributes, and the attribute-beats-config rule
//   widget-config.ts  the agent's server-side config and its loader
//   terms.ts          the pre-call terms gate
//   call-controls.ts  the in-call panel
//   transcript.ts     the live transcript
//   telemetry.ts      anonymous operational reporting
//
// — so that changing how the widget looks, what it remembers, or what it
// reports does not mean editing the same file as when a call starts.

import {
  OBSERVED_ATTRIBUTES,
  readBooleanAttribute,
  readDynamicVariables,
  readOverrides,
  resolveAppearance,
  telemetryDisabled,
  type ResolvedAppearance,
} from "./attributes.js";
import { resolveApiBase } from "./api-base.js";
import { buildCallControlsDOM, CallControls, type CallControlsElements } from "./call-controls.js";
import { errorCodeOf, userFacingMessage, WidgetError } from "./errors.js";
import { type ConnectFn, loadConnect, loadMarkdown } from "./lazy.js";
import { MIC_ICON, MIC_MUTED_ICON, STYLES } from "./styles.js";
import { Telemetry } from "./telemetry.js";
import {
  alreadyAccepted,
  buildTermsDOM,
  rememberAcceptance,
  TermsGate,
  termsStorageKey,
  type TermsElements,
} from "./terms.js";
import { TextChatController } from "./text.js";
import type { TextChatTransport } from "./text.js";
import { TextChatPanel } from "./text-panel.js";
import { TranscriptPanel } from "./transcript.js";
import type { AgentHandle, AgentStatus, SessionProvider } from "./types.js";
import { widgetVersion } from "./version.js";
import { WidgetConfigLoader } from "./widget-config.js";

const TAG = "speechify-agent";

// How long the widget stays hidden waiting on its config before revealing
// itself anyway. Long enough that a normal fetch wins the race and the
// customer never sees unstyled defaults; short enough that a dead origin
// does not leave an invisible embed on the page.
const REVEAL_DEADLINE_MS = 2_000;

/**
 * The inputs that decide WHICH agent a call actually reaches, captured when
 * the gate is decided so the connect cannot be pointed somewhere else after
 * the visitor has consented.
 *
 * `connectAgent` picks the first of these that is set - provider, then token +
 * url, then agent id - so a move in ANY of them retargets a call just as
 * surely as swapping the agent, and none of them is visible to a visitor
 * reading the terms.
 */
interface CallSubject {
  agentId: string | null;
  sessionToken: string | null;
  sessionUrl: string | null;
  /**
   * In here because an agent id only names an agent relative to an origin: the
   * terms were read from `{apiBase}/v1/agents/{id}/widget-config`, so minting
   * at a different origin can reach a different agent under the same id.
   *
   * Unlike the others it is not an observed attribute, so a move cannot close
   * the modal - only fail the check on the way out. That is the half that
   * matters, and widening the observed set would change what a config
   * invalidation means for a new origin, which is not this fix's business.
   */
  apiBase: string | null;
  /**
   * Pinned, but deliberately NOT compared by `#stillOnSubject`. Function
   * identity is not stable the way an attribute string is: a framework that
   * re-creates the closure on each render would make every attempt spanning a
   * re-render look like a retarget and abandon it. Pinning alone closes the
   * hole without that false positive - it never rejects anything, it just
   * connects through the provider consent was given under.
   */
  sessionProvider: SessionProvider | null;
}

/** Attributes that move the subject, and so invalidate an attempt in flight. */
const SUBJECT_ATTRIBUTES = new Set(["agent-id", "session-token", "session-url"]);

// Button label for text mode (mode="text"). `start-call-text` still overrides
// it so one embed can relabel either modality.
const DEFAULT_TEXT_START_TEXT = "Chat with agent";

/** The shadow-DOM references the renderer writes to. Built once, on mount. */
interface Chrome {
  button: HTMLButtonElement;
  orbWrap: HTMLSpanElement;
  orb: HTMLSpanElement;
  label: HTMLSpanElement;
  hint: HTMLSpanElement;
  srStatus: HTMLDivElement;
  transcript: TranscriptPanel;
  controls: CallControlsElements;
  terms: TermsElements;
  // Text-chat mode (mode="text", against a text-channel agent): an
  // independent, absolutely-positioned sibling of the call panel (not nested
  // in it) so it carries its own chrome without doubling the panel's.
  textPanel: TextChatPanel;
}

// The speeds the control steps through. They live here, in the embed, rather
// than in the agent: the ladder is a UI decision, and the agent takes a number.
// It reaches the fastest the agent renders (4.5x) because people who listen at
// speed do, and it steps coarser as it climbs - the difference between 1x and
// 1.25x is worth a press, the difference between 3x and 3.25x is not.
const SPEED_STEPS = [1, 1.25, 1.5, 2, 3, 4.5, 0.75] as const;

class SpeechifyAgentElement extends HTMLElement {
  static observedAttributes = OBSERVED_ATTRIBUTES;

  // Null until connectedCallback builds the shadow tree. Every renderer path
  // guards on it, which is what makes an attribute change before mount safe.
  #chrome: Chrome | null = null;
  #controls: CallControls | null = null;
  #termsGate: TermsGate | null = null;

  #handle: AgentHandle | null = null;
  /**
   * Which attempt the element currently belongs to, bumped once per start.
   *
   * A call does not stop when the element stops wanting it. The transport
   * notices an abort only at its next await, `handle.stop()` takes a round
   * trip, and both emit their own callbacks on the way down — while the widget
   * is already clickable and may already be on the next call. Everything an
   * attempt does asynchronously therefore carries the generation it was
   * started under, and does nothing once that is no longer the current one.
   */
  #attempt = 0;
  #sessionProvider: SessionProvider | null = null;
  #status: AgentStatus = "idle";
  #micEnabled = true;
  /** Aborts an in-flight connect. Non-null only while connecting. */
  #connectAbort: AbortController | null = null;
  #revealTimer: ReturnType<typeof setTimeout> | undefined;
  /** End-user copy for the current error, set by #fail from the typed error. */
  #errorHint = "Something went wrong starting the call.";
  /**
   * True while the terms gate is deciding. Distinct from the gate being open:
   * it also covers the config fetch and the markdown chunk load that precede
   * it, during all of which the button must stay disabled.
   *
   * Cleared the moment the gate has an answer, and deliberately not held for
   * the connect that follows: from there `connecting` is what disables the
   * button, and it is the status an abandoned attempt can actually clear.
   */
  #termsPending = false;
  /**
   * Whether `speechify-agent:call` has fired for the current toggle cycle.
   * Resets when the session ends so the next start fires it again.
   */
  #callEventFired = false;
  /**
   * Set once per toggle cycle so a reconnect mid-call is not a second
   * "connected"; reset when the session ends.
   */
  #connectedReported = false;

  readonly #config = new WidgetConfigLoader({
    onLoaded: (cfg) => {
      // Absent means on; only an explicit false is an opt-out, and the
      // reporter remembers that answer so a later fetch failure cannot undo it.
      this.#telemetry?.applyRemoteConfig(cfg.telemetry?.enabled !== false);
      this.#applyOrbColors();
      this.#render();
    },
    onUnavailable: () => this.#telemetry?.configUnavailable(),
    onWarn: (...args) => this.#warn(...args),
  });

  /**
   * Anonymous operational reporting (AIS-5357). Created on mount so the mount
   * itself is reportable, and torn down on detach. Nothing here is a
   * dependency of the widget working: every call site is a bare `?.`, and the
   * reporter is silent until it knows the embedder has not opted out.
   */
  #telemetry: Telemetry | null = null;

  // ── Text-chat mode (mode="text") ──
  #textController: TextChatController | null = null;
  #textTransport: TextChatTransport | null = null;
  // Whether the text panel is open. Voice mode drives its panel off
  // #controls.isOpen; text mode has no CallControls, so it owns this flag.
  #textOpen = false;

  /**
   * Attributes and config resolved together, recomputed on read rather than
   * cached. The inputs are a handful of `getAttribute` calls and an object
   * already in memory, so recomputing costs nothing measurable — and it
   * removes the invalidation problem a cache would introduce, in a class
   * whose state changes from four different directions.
   */
  get #appearance(): ResolvedAppearance {
    return resolveAppearance(this, this.#config.config);
  }

  get sessionProvider(): SessionProvider | null {
    return this.#sessionProvider;
  }

  set sessionProvider(fn: SessionProvider | null) {
    this.#sessionProvider = fn;
  }

  /**
   * Anonymous operational reporting, "on" (default) or "off". The property
   * mirrors the `telemetry` attribute so a framework that only ever sets
   * properties can switch it off without touching HTML.
   */
  // Injected text-chat transport (mode="text"). The host (console preview or
  // a customer's page) sets this to an object that holds the credential and
  // talks to the text channel, so the widget never sees an API key. See
  // README.md "Text mode: the injected transport seam".
  get textChatTransport(): TextChatTransport | null {
    return this.#textTransport;
  }

  set textChatTransport(t: TextChatTransport | null) {
    this.#textTransport = t;
  }

  get telemetry(): "on" | "off" {
    return telemetryDisabled(this) ? "off" : "on";
  }

  set telemetry(v: "on" | "off") {
    if (v === "off") this.setAttribute("telemetry", "off");
    else this.removeAttribute("telemetry");
  }

  connectedCallback() {
    // Before the early-return below: an SPA that moves the element re-enters
    // here with the shadow root already built, and everything past that guard
    // runs exactly once in the element's life. The reporter is torn down on
    // detach, so it has to be re-armed here or a moved widget goes silent for
    // the rest of the page.
    this.#startTelemetry();
    if (this.shadowRoot) return; // already initialised

    const shadow = this.attachShadow({ mode: "open" });
    this.#chrome = this.#buildChrome(shadow);
    this.#controls = new CallControls(this, this.#chrome.controls, () => this.#render());
    this.#termsGate = new TermsGate(
      {
        shadowRoot: this.shadowRoot,
        isConnected: this.isConnected,
        fallbackFocus: () => this.#chrome?.button ?? null,
      },
      this.#chrome.terms,
    );

    this.#applyOrbColors();
    this.#render();
    this.#revealWhenConfigured();
  }

  disconnectedCallback() {
    this.#termsGate?.close(false);
    this.#controls?.close();
    this.#chrome?.transcript.hide();
    this.#chrome?.transcript.clear();
    clearTimeout(this.#revealTimer);
    this.#revealTimer = undefined;
    // The handle only exists once connectAgent has resolved, so stopping it
    // is not enough on its own: for the whole connect window — which spans a
    // microphone prompt the user may sit on — there is nothing to stop, and
    // the session that lands afterwards would keep a live room and a
    // published microphone with no element left to end it.
    this.#connectAbort?.abort();
    this.#connectAbort = null;
    void this.#handle?.stop();
    this.#handle = null;
    this.#speed = 0;
    // Detaching is letting go of the call, so nothing that session reports on
    // its way down may touch this element - including after it is re-attached.
    this.#attempt++;
    // Re-attaching skips connectedCallback's setup (the shadow root already
    // exists), so a status left at "listening" would render an in-call label
    // over a session that no longer exists.
    this.#status = "idle";
    this.#callEventFired = false;
    this.#connectedReported = false;
    this.#micEnabled = true;
    this.#telemetry?.dispose();
    this.#telemetry = null;
    // Drop the text conversation reference; a remount starts fresh.
    this.#textController = null;
    this.#textOpen = false;
    this.#chrome?.textPanel.hide();
  }

  attributeChangedCallback(name: string, previous: string | null, next: string | null): void {
    // Swapping the agent must drop the previous agent's cached config.
    // Without this an SPA that reuses one element applied agent A's terms
    // gate and appearance to a session minted for agent B.
    // Any subject move voids an attempt in flight, so an open gate is asking
    // a question that can no longer be answered: leaving it up invites the
    // visitor to accept terms for a call this element will refuse to start.
    if (SUBJECT_ATTRIBUTES.has(name) && previous !== next) {
      this.#termsGate?.close(false);
      this.#abandonCall();
    }

    if (name === "agent-id" && previous !== null && previous !== next) {
      this.#config.invalidate();
      this.#telemetry?.setAgentId(next ?? undefined);
      void this.#loadConfig();
    }
    if (name === "telemetry") {
      this.#telemetry?.setLocalOptOut(telemetryDisabled(this));
    }
    if (name === "avatar-orb-color-1" || name === "avatar-orb-color-2" || name === "avatar-image-url") {
      this.#applyOrbColors();
    }
    if (this.#chrome) this.#render();
  }

  // ── Setup ──────────────────────────────────────────────────────────────

  /** Builds the shadow tree and returns the references the renderer writes to. */
  #buildChrome(shadow: ShadowRoot): Chrome {
    const style = document.createElement("style");
    style.textContent = STYLES;
    shadow.appendChild(style);

    const button = document.createElement("button");
    button.type = "button";
    button.addEventListener("click", () => void this.#toggle());

    // Status announcements live here rather than on the button. A live region
    // ON an interactive control makes a screen reader re-announce the control
    // itself on every label change, which during a call is every agent state
    // transition.
    const srStatus = document.createElement("div");
    srStatus.className = "sr-status";
    srStatus.setAttribute("role", "status");
    srStatus.setAttribute("aria-live", "polite");
    shadow.appendChild(srStatus);

    const orbWrap = document.createElement("span");
    orbWrap.className = "orb-wrap";
    const orb = document.createElement("span");
    orb.className = "orb";
    orbWrap.appendChild(orb);
    button.appendChild(orbWrap);

    const label = document.createElement("span");
    button.appendChild(label);
    shadow.appendChild(button);

    const transcript = new TranscriptPanel(shadow);

    const controls = buildCallControlsDOM(transcript.element, {
      onToggleMute: () => void this.#toggleMute(),
      onCycleSpeed: () => void this.#cycleSpeed(),
      onEnd: () => {
        // During connect there is no handle yet, so this is the only control
        // that can abandon a connect that is taking too long. Without it the
        // start button is disabled too and a reload is the only way out.
        if (this.#connectAbort) {
          this.#cancelConnect();
          return;
        }
        void this.#stopSession();
      },
      onDismiss: () => {
        this.#controls?.close();
        this.#chrome?.button.focus();
      },
    });
    shadow.appendChild(controls.popup);

    const hintWrap = document.createElement("div");
    const hint = document.createElement("span");
    hint.className = "hint";
    hintWrap.appendChild(hint);
    shadow.appendChild(hintWrap);

    const terms = buildTermsDOM((accepted) => this.#termsGate?.close(accepted));
    shadow.appendChild(terms.overlay);

    const textPanel = new TextChatPanel(shadow);
    textPanel.onSubmit((text) => void this.#textController?.send(text));
    shadow.appendChild(textPanel.element);

    return { button, orbWrap, orb, label, hint, srStatus, transcript, controls, terms, textPanel };
  }

  /**
   * Keeps the widget hidden until its config lands, so the customer never
   * sees unstyled defaults, but never longer than the deadline: `:host` is
   * opacity 0 and pointer-events none until `resolved`, so gating the reveal
   * solely on the fetch would leave a black-holed origin showing an invisible,
   * unclickable embed with nothing logged.
   */
  #revealWhenConfigured(): void {
    const reveal = () => this.setAttribute("resolved", "");
    if (!this.getAttribute("agent-id")) {
      reveal();
      return;
    }
    this.#revealTimer = setTimeout(reveal, REVEAL_DEADLINE_MS);
    void this.#loadConfig().finally(() => {
      clearTimeout(this.#revealTimer);
      this.#revealTimer = undefined;
      reveal();
    });
  }

  #loadConfig(): Promise<void> {
    return this.#config.load(this.getAttribute("agent-id"), this.getAttribute("api-base"));
  }

  /**
   * Stands up the anonymous reporter and records the mount.
   *
   * Wrapped because it runs inside connectedCallback: an exception here —
   * from a host page that has replaced localStorage, frozen navigator, or
   * shimmed performance — would abort element setup and leave a dead widget
   * on the customer's page. Reporting is a diagnostic; it does not get to
   * take the product down.
   */
  #startTelemetry(): void {
    if (this.#telemetry) return;
    try {
      const t = new Telemetry({
        apiBase: resolveApiBase(this.getAttribute("api-base")),
        bundleVersion: widgetVersion(),
      });
      t.setLocalOptOut(telemetryDisabled(this));
      const agentId = this.getAttribute("agent-id");
      t.setAgentId(agentId ?? undefined);
      this.#telemetry = t;
      t.record("widget.mounted");

      // The reporter sends nothing until the agent's setting is resolved, so
      // something must always resolve it. Three ways in: we already have the
      // config (a reattach), there is no agent whose config to read (a
      // session-token embed), or a fetch is due — load() is guarded, so
      // asking for it again here is free and covers the reattach case where
      // the reveal path never runs.
      if (this.#config.loaded) t.applyRemoteConfig(this.#config.config?.telemetry?.enabled !== false);
      else if (!agentId) t.configUnavailable();
      else void this.#loadConfig();
    } catch {
      this.#telemetry = null;
    }
  }

  // ── The call ───────────────────────────────────────────────────────────

  async #toggle() {
    const chrome = this.#chrome;
    if (!chrome || chrome.button.disabled || this.#termsPending) return;
    if (this.#isTextMode()) {
      await this.#toggleText();
      return;
    }
    if (this.#handle) {
      // Active session: toggle the controls panel.
      if (this.#controls?.isOpen) this.#controls.close();
      else this.#controls?.open();
      return;
    }

    this.#termsPending = true;
    chrome.button.disabled = true;
    let authorized: CallSubject | null;
    try {
      authorized = await this.#authorizeCall();
    } finally {
      this.#termsPending = false;
      this.#render();
    }
    if (!authorized) return;

    const generation = ++this.#attempt;
    this.#status = "connecting";
    this.#callEventFired = false;
    this.#connectedReported = false;
    this.#micEnabled = true;
    this.#speed = 0;
    chrome.transcript.clear();
    this.#telemetry?.record("widget.call_requested");
    // Armed before the first render: the cancel control's enabled state is
    // derived from it, so creating it later renders a disabled button for
    // exactly the window in which cancelling is the only way out.
    const abort = new AbortController();
    this.#connectAbort = abort;
    this.#controls?.open();
    this.#render();

    // Every input to this attempt is read ONCE, here, before the first
    // await below - and none of them may be re-read past it.
    //
    // Everything that names the callee comes from the authorization rather
    // than from the attributes: that is the subject the gate was decided
    // against, and re-reading could only ever disagree with it.
    // `loadConnect()` then awaits a network fetch of the realtime chunk, and
    // an SPA is free to swap attributes during it - a swap past this line
    // retargets nothing, because the attempt is already bound to what the
    // visitor consented to.
    const attempt = {
      agentId: authorized.agentId ?? undefined,
      sessionToken: authorized.sessionToken ?? undefined,
      sessionUrl: authorized.sessionUrl ?? undefined,
      apiBase: authorized.apiBase ?? undefined,
      userIdentity: this.getAttribute("user-identity") ?? undefined,
      overrides: readOverrides(this),
      dynamicVariables: readDynamicVariables(this, (...args) => this.#warn(...args)),
    };
    const { sessionToken, sessionUrl, overrides, dynamicVariables } = attempt;

    // Pre-minted sessions (session-token + session-url) and sessionProvider
    // both bypass `fetchSession`, so `override-*` / `dynamic-variables`
    // attributes are silently dropped.
    if ((overrides || dynamicVariables) && (authorized.sessionProvider || (sessionToken && sessionUrl))) {
      this.#warn(
        "<speechify-agent> override-* / dynamic-variables attributes are ignored when sessionProvider or session-token + session-url are supplied. " +
          "Bake those overrides into the session server-side (POST /v1/agents/{id}/sessions) before minting the token.",
      );
    }

    try {
      // Typed here rather than left as the loader's bare TypeError, so a
      // visitor whose network dropped the realtime chunk is told to retry
      // instead of reading the catch-all, and the failure reports as itself.
      let connect: ConnectFn;
      try {
        connect = await loadConnect();
      } catch (err) {
        throw new WidgetError("session_unavailable", "Could not load the call. Check your connection and try again.", {
          cause: err,
        });
      }
      const handle = await connect({
        ...attempt,
        sessionProvider: authorized.sessionProvider ?? undefined,
        debug: this.#debug(),
        signal: abort.signal,
        onTelemetry: (name, detail) => {
          if (detail?.conversationId) this.#telemetry?.setConversationId(detail.conversationId);
          this.#telemetry?.record(name, { errorCode: detail?.errorCode });
        },
        // A session keeps talking while it is being torn down - stopping it
        // reports `ended`, and a dying transport reports errors - so a
        // superseded attempt's callbacks arrive with the widget already on the
        // next call. Unguarded, that `ended` nulls the REPLACEMENT's handle and
        // orphans its room and microphone, which is the leak this whole file
        // exists to prevent.
        onStatus: (s) => {
          if (this.#owns(generation)) this.#onStatus(s);
        },
        // The agent is the authority on what the call is running at, so the
        // control follows it - including a change this client did not make.
        onSpeed: (speed) => {
          if (!this.#owns(generation)) return;
          this.#speed = speed;
          this.#render();
        },
        onTranscript: (msgs) => {
          if (this.#owns(generation) && this.#appearance.transcriptEnabled) {
            chrome.transcript.update(msgs, this.#status);
          }
        },
        onMessage: (m) => {
          if (this.#owns(generation)) this.dispatchEvent(new CustomEvent("message", { detail: m }));
        },
        // Failures the connect itself raises arrive at the catch below, never
        // here: this is only the post-connect channel (a lost connection,
        // blocked audio), so the two never report the same failure twice.
        onError: (err) => {
          if (!this.#owns(generation)) return;
          this.#status = "error";
          this.#render();
          this.#fail(err);
        },
      });

      // The element can be removed, the user can cancel, or the page can point
      // the widget at a different agent, while the await above is still
      // outstanding. Either way the session that just landed has no owner, so
      // end it rather than leaking a live room and an open microphone. Only the
      // session is dealt with here: whoever abandoned the attempt has already
      // put the widget where it wants it, and may since have started another.
      if (abort.signal.aborted || !this.isConnected || !this.#owns(generation)) {
        void handle.stop();
      } else {
        this.#handle = handle;
      }
    } catch (err) {
      // Silent for an attempt the element let go of: an abort thrown by our own
      // teardown is not a failure, and an attempt something newer replaced no
      // longer has a widget to put an error state on.
      if (!abort.signal.aborted && this.#owns(generation)) {
        this.#handle = null;
        this.#callEventFired = false;
        this.#controls?.close();
        this.#status = "error";
        this.#fail(err);
      }
    } finally {
      if (this.#connectAbort === abort) this.#connectAbort = null;
      this.#render();
    }
  }

  /** Whether the attempt started as `generation` is still the element's own. */
  #owns(generation: number): boolean {
    return this.#attempt === generation;
  }

  #onStatus(s: AgentStatus): void {
    this.#status = s;
    if (!this.#connectedReported && (s === "listening" || s === "speaking")) {
      this.#connectedReported = true;
      this.#telemetry?.record("widget.connected");
    }
    if (s === "ended") {
      this.#handle = null;
      this.#callEventFired = false;
      this.#connectedReported = false;
      this.#micEnabled = true;
      this.#controls?.close();
      this.#telemetry?.record("widget.ended");
    }
    if (this.#handle && this.#appearance.transcriptEnabled) {
      this.#chrome?.transcript.update(this.#handle.messages, s);
    }
    this.#render();
    this.dispatchEvent(new CustomEvent("status", { detail: s }));
    // Fire the public `speechify-agent:call` event exactly once per toggle
    // cycle, the first time we reach an in-call status. Bubbles so pages can
    // delegate-listen on an ancestor.
    if (!this.#callEventFired && (s === "listening" || s === "speaking")) {
      this.#callEventFired = true;
      this.dispatchEvent(
        new CustomEvent("speechify-agent:call", { bubbles: true, composed: true, detail: { status: s } }),
      );
    }
  }

  /**
   * Ends the call this element is running, because the element no longer names
   * the agent that call is for.
   *
   * Pinning (AIS-5454) stops a subject move from *retargeting* an attempt, but
   * an attempt it cannot retarget it also cannot stop: the widget went on
   * connecting to the superseded agent while reporting the new one, with the
   * start button held disabled by a status nothing would clear.
   *
   * Ending rather than rebinding, because the new agent may have terms of its
   * own and the visitor has clicked nothing: they get the idle widget back and
   * the next click runs the new agent's gate from the top. Same reasoning as
   * the gate takes, and the same cost — one click.
   *
   * A live call is ended for the same reason as a connect, and deliberately
   * not treated as the gentler case. Which of the two a swap lands in is a
   * race on the transport that no host page can see or control, so sparing the
   * live one would make the behaviour depend on an instant nobody can aim at:
   * the same swap would abandon the call or keep it, by milliseconds.
   */
  #abandonCall(): void {
    if (this.#connectAbort) this.#cancelConnect();
    else if (this.#handle) void this.#stopSession();
  }

  /** Abandon an in-flight connect, returning the widget to idle. */
  #cancelConnect() {
    if (!this.#connectAbort) return;
    this.#connectAbort.abort();
    this.#connectAbort = null;
    // Speaks for nobody from here. A transport dying on the way down still
    // reports its error, and the visitor who just cancelled should not be
    // shown one.
    this.#attempt++;
    this.#status = "idle";
    this.#controls?.close();
    this.#render();
  }

  async #stopSession() {
    // Capture and null synchronously so a rapid second click is a no-op and a
    // transport error during `stop()` still leaves the widget in a clean
    // state — the session is unrecoverable either way.
    const handle = this.#handle;
    if (!handle) return;
    this.#handle = null;
    // `stop()` is a round trip, and ending a call is now something an
    // attribute change can trigger, so the visitor can start the next call
    // before this one has finished going down. Putting the widget back to idle
    // afterwards would then reset a call that is already live and leave its
    // room running behind idle chrome.
    const generation = this.#attempt;
    try {
      await handle.stop();
    } catch (err) {
      if (this.#owns(generation)) this.#fail(err);
    } finally {
      if (this.#owns(generation)) {
        this.#status = "idle";
        this.#callEventFired = false;
        this.#micEnabled = true;
        this.#controls?.close();
        this.#chrome?.transcript.hide();
        this.#render();
      }
    }
  }

  // The speed the agent says it is running at: 0 until it publishes one,
  // which is also what keeps the control hidden on a call that cannot change
  // it (a voice with no stretcher of ours, an older worker). Reset per
  // attempt: a second call on the same element would otherwise open showing
  // the first call's speed, and keep showing it against an agent that never
  // publishes one.
  #speed = 0;

  async #cycleSpeed() {
    const handle = this.#handle;
    if (!handle || !this.#speed) return;
    const generation = this.#attempt;
    const current = this.#speed;
    const step = (SPEED_STEPS.findIndex((s) => s === current) + 1) % SPEED_STEPS.length;
    // The ladder is non-empty, so the index always names one; the fallback is
    // for the type, and for a speed the agent set outside the ladder.
    const next = SPEED_STEPS[step] ?? SPEED_STEPS[0];
    // No optimistic paint here, unlike mute: the agent answers with the speed
    // it applied, which is not always the one asked for, and showing the ask
    // first would flash a number the call never ran at.
    try {
      const applied = await handle.setSpeed(next);
      if (!this.#owns(generation)) return;
      this.#speed = applied;
      this.#render();
    } catch (err) {
      if (!this.#owns(generation)) return;
      this.#fail(err);
    }
  }

  async #toggleMute() {
    const handle = this.#handle;
    if (!handle) return;
    const generation = this.#attempt;
    const next = !this.#micEnabled;
    this.#micEnabled = next;
    this.#render();
    try {
      await handle.setMicEnabled(next);
    } catch (err) {
      // The call can end inside that round trip, and since a subject swap ends
      // one, it can be replaced inside it too. Rolling the toggle back then
      // would report a different call's microphone as still muted, and show
      // that call an error raised against a session it never had.
      if (!this.#owns(generation)) return;
      this.#micEnabled = !next;
      this.#render();
      this.#fail(err);
    }
  }

  // ── Terms gate ─────────────────────────────────────────────────────────

  #callSubject(): CallSubject {
    return {
      agentId: this.getAttribute("agent-id"),
      sessionToken: this.getAttribute("session-token"),
      sessionUrl: this.getAttribute("session-url"),
      apiBase: this.getAttribute("api-base"),
      sessionProvider: this.#sessionProvider,
    };
  }

  /**
   * True while this attempt is still about the agent it started out about.
   *
   * Every await between the click and the mint is a window in which an SPA
   * can move the subject on a reused element. An attempt that survives one of
   * those windows would carry one agent's consent into another agent's call,
   * so each resume point re-asks this question instead of trusting what it
   * read before the await.
   *
   * Only the attributes are compared; `sessionProvider` is pinned instead, for
   * the reason on `CallSubject`.
   */
  #stillOnSubject(subject: CallSubject): boolean {
    if (!this.isConnected) return false;
    const now = this.#callSubject();
    return (
      now.agentId === subject.agentId &&
      now.sessionToken === subject.sessionToken &&
      now.sessionUrl === subject.sessionUrl &&
      now.apiBase === subject.apiBase
    );
  }

  /**
   * Decides whether this attempt may start, and pins it to one subject.
   *
   * Returns the subject the terms were checked against - the same one the
   * session must then be opened for - or null when the call must not start.
   * Consent and session naming the same subject is the whole invariant here:
   * a gate that validates agent A and authorizes a call to agent B is worse
   * than no gate, because it reports itself as satisfied.
   *
   * A move before the mint abandons the attempt silently rather than failing
   * it. Nothing went wrong - the page simply changed its mind - so the widget
   * returns to idle and the next click runs the new subject's gate from the
   * top. That costs a click when a page merely rotates a pre-minted token
   * mid-modal, which is the right way round: the widget cannot read a token,
   * so it cannot tell a rotation from a retarget.
   */
  async #authorizeCall(): Promise<CallSubject | null> {
    const subject = this.#callSubject();
    const { agentId } = subject;
    await this.#loadConfig();
    if (!this.#stillOnSubject(subject)) return null;

    // A failed widget-config fetch leaves us unable to tell "no terms
    // configured" from "we could not ask", so treating it as "no terms" would
    // silently bypass a configured gate. Fail closed — but only where the
    // widget is itself the gate. With a sessionProvider or a pre-minted token
    // the embedder's own backend authorizes the call, and an anonymous
    // widget-config rejection is the documented expected state for a private
    // agent, so blocking there bricked correctly-built embeds for no gain.
    const widgetMintsSession = !!agentId && !subject.sessionProvider && !subject.sessionToken;
    if (widgetMintsSession && !this.#config.loaded) {
      this.#status = "error";
      this.#render();
      // Report the cause rather than a message about terms. A 403 here is an
      // origin or hostname allowlist rejection, which is both the most common
      // failure and the one the site owner can actually fix; session minting
      // would fail the same way, so the call could never have started.
      const status = this.#config.failureStatus;
      this.#fail(
        status === 403 || status === 401
          ? new WidgetError("session_rejected", "This agent is not available on this site.", { status })
          : new WidgetError("session_unavailable", "Could not reach the agent. Check your connection and try again.", {
              status,
            }),
      );
      return null;
    }

    const terms = this.#config.config?.terms;
    if (!terms?.enabled) return subject;
    const content = (terms.content ?? "").trim();
    if (!content) return subject;

    const key = termsStorageKey(agentId, content);
    if (alreadyAccepted(key)) return subject;

    if (!(await this.#promptTerms(content, subject))) return null;
    rememberAcceptance(key);
    return subject;
  }

  /**
   * Shows `content` and returns whether the visitor accepted it *for
   * `subject`*. A declined answer and an answer that arrived after the subject
   * moved on are the same result on purpose: both mean this attempt holds no
   * consent.
   */
  async #promptTerms(content: string, subject: CallSubject): Promise<boolean> {
    const gate = this.#termsGate;
    if (!gate) return false;

    // Fetched only now: the renderer exists solely for this modal, which most
    // agents never enable, so it has no business in the initial payload.
    let renderMarkdown: (input: string) => string;
    try {
      renderMarkdown = await loadMarkdown();
    } catch (err) {
      // A failed chunk fetch must decline the gate, not escape as an
      // unhandled rejection out of the click handler. Declining is the safe
      // direction: the call does not start, and the terms were never shown.
      this.#status = "error";
      this.#render();
      this.#fail(
        new WidgetError("session_unavailable", "Could not load the terms. Check your connection and try again.", {
          cause: err,
        }),
      );
      return false;
    }

    // The element can be removed, or the subject moved, while that fetch is
    // outstanding. Showing the modal anyway would either attach it to a
    // detached tree or show one agent's terms before a different agent's call.
    if (!this.#stillOnSubject(subject)) return false;
    const accepted = await gate.prompt(renderMarkdown(content));
    if (!accepted) return false;

    // The visitor can sit on the modal indefinitely, so this is the widest
    // window of the lot. attributeChangedCallback closes the gate on a subject
    // move, which covers the visitor who is still reading - but a host page
    // that swaps from its own click handler gets there in the SAME task as
    // Accept, after this promise has already resolved. Re-checking is what
    // makes acceptance mean "for this agent" rather than "at this moment".
    //
    // The acceptance is dropped rather than banked under `subject`: consent
    // collected while the widget was being swapped out from under the visitor
    // is not consent worth remembering, and the cost of being wrong is one
    // extra modal.
    return this.#stillOnSubject(subject);
  }

  // ── Diagnostics ────────────────────────────────────────────────────────

  #debug(): boolean {
    return readBooleanAttribute(this, "debug");
  }

  #warn(...args: unknown[]): void {
    if (!this.#debug()) return;
    // eslint-disable-next-line no-console
    console.warn(...args);
  }

  /**
   * Surface an error to the host page (the `error` event) and the console.
   * The error-state hint reads "Check the console", so the log is
   * unconditional, unlike #warn, which is gated on `debug` for routine
   * integration noise. Does not touch `#status`: callers that want the error
   * UI set it and re-render themselves.
   */
  #fail(err: unknown): void {
    const e = err instanceof Error ? err : new Error(String(err));
    this.#errorHint = userFacingMessage(e);
    // Reported here rather than at each throw site so this counts exactly
    // what it claims to: the times a visitor was actually shown an error. It
    // deliberately overlaps the specific failure events — those say what went
    // wrong, this says how often anything did — so the two are read together,
    // never summed.
    this.#telemetry?.record("widget.error", { errorCode: errorCodeOf(e) });
    // eslint-disable-next-line no-console
    console.error("[speechify-agent]", e);
    this.dispatchEvent(new CustomEvent("error", { detail: e }));
  }

  // ── Rendering ──────────────────────────────────────────────────────────

  /**
   * Applies the resolved avatar to the orb and the host's CSS custom
   * properties. Separate from #render because it rebuilds DOM rather than
   * toggling classes, and only the three avatar attributes and a config load
   * can change it.
   */
  #applyOrbColors(): void {
    const chrome = this.#chrome;
    if (!chrome) return;
    const { orbColor1, orbColor2, avatarImageUrl } = this.#appearance;

    // Inline styles outrank external stylesheet rules, so when neither the
    // attribute nor the config supplies a colour we clear the inline property
    // and let the `:host` block in STYLES (or a page stylesheet) take over.
    if (orbColor1 !== null) this.style.setProperty("--speechify-agent-orb-1", orbColor1);
    else this.style.removeProperty("--speechify-agent-orb-1");
    if (orbColor2 !== null) this.style.setProperty("--speechify-agent-orb-2", orbColor2);
    else this.style.removeProperty("--speechify-agent-orb-2");

    chrome.orb.replaceChildren();
    if (!avatarImageUrl) {
      chrome.orb.innerHTML = MIC_ICON;
      return;
    }
    const img = document.createElement("img");
    img.src = avatarImageUrl;
    img.alt = "";
    img.setAttribute("aria-hidden", "true");
    // If the image fails to load, fall back to the mic icon so the button is
    // never blank.
    img.addEventListener("error", () => {
      chrome.orb.replaceChildren();
      chrome.orb.innerHTML = MIC_ICON;
    });
    chrome.orb.appendChild(img);
  }

  /** Copy for the current status, from the resolved override chain. */
  #labelFor(text: ResolvedAppearance["text"]): string {
    switch (this.#status) {
      case "idle":
      case "ended":
        return text.start;
      case "connecting":
        return "Connecting...";
      case "reconnecting":
        return "Reconnecting...";
      case "listening":
        return text.listening;
      case "thinking":
        return text.thinking;
      case "speaking":
        return text.speaking;
      case "error":
        return "Retry";
    }
  }

  #render() {
    const chrome = this.#chrome;
    if (!chrome) return;
    if (this.#isTextMode()) {
      this.#renderText(chrome);
      return;
    }

    const active = this.#status === "listening" || this.#status === "thinking" || this.#status === "speaking";
    // Reconnecting is still an in-call state: the panel and its controls must
    // stay up, and the connecting shimmer communicates that the transport is
    // working on it rather than that the call is healthy.
    const connecting = this.#status === "connecting" || this.#status === "reconnecting";
    const error = this.#status === "error";
    const appearance = this.#appearance;
    const { fab } = appearance;

    chrome.button.classList.toggle("active", active);
    chrome.button.classList.toggle("connecting", connecting);
    chrome.button.classList.toggle("fab", fab);
    chrome.button.disabled = connecting || this.#termsPending;

    chrome.transcript.setMode(fab ? "fab" : "pill");

    const panelVisible = !!this.#controls?.isOpen && (!!this.#handle || connecting);
    chrome.controls.popup.classList.toggle("open", panelVisible);
    chrome.controls.popup.classList.toggle("pill-pos", !fab);

    // The host needs relative positioning for the absolutely-positioned panel.
    if (panelVisible) this.style.position = "relative";
    else this.style.removeProperty("position");

    if (panelVisible && appearance.transcriptEnabled) chrome.transcript.show();
    else chrome.transcript.hide();
    chrome.controls.orbDisplay.style.display = panelVisible && !appearance.transcriptEnabled ? "flex" : "none";

    this.#paintOrb(chrome.controls.orb, chrome.controls.orbWrap, false);

    chrome.controls.mute.classList.toggle("muted", !this.#micEnabled);
    chrome.controls.mute.innerHTML = this.#micEnabled ? MIC_ICON : MIC_MUTED_ICON;
    chrome.controls.mute.setAttribute("aria-label", this.#micEnabled ? "Mute microphone" : "Unmute microphone");
    chrome.controls.mute.disabled = !this.#handle;

    const speedVisible = appearance.speedControlEnabled && this.#speed > 0;
    chrome.controls.speed.hidden = !speedVisible;
    if (speedVisible) {
      chrome.controls.speed.textContent = `${this.#speed}x`;
      chrome.controls.speed.setAttribute("aria-label", `Speaking speed ${this.#speed}x, change`);
      chrome.controls.speed.disabled = !this.#handle;
    }

    chrome.controls.end.disabled = !this.#handle && !this.#connectAbort;
    chrome.controls.end.textContent = this.#handle ? "End call" : "Cancel";

    // Only fall back to the config theme when the host has no explicit
    // `widget-theme` attribute. setAttribute fires attributeChangedCallback
    // even when the value is unchanged, and the callback re-enters #render —
    // without the guard inside resolveAppearance, every render recurses until
    // the call stack overflows.
    if (appearance.themeFromConfig) this.setAttribute("widget-theme", appearance.themeFromConfig);

    const label = this.#labelFor(appearance.text);
    chrome.label.textContent = fab ? "" : label;
    chrome.label.style.display = fab ? "none" : "";

    this.#paintOrb(chrome.orb, chrome.orbWrap, true);

    chrome.button.setAttribute("aria-label", label);
    // End-user copy: this renders on someone else's site, where telling the
    // visitor to open devtools is useless. The typed error carries a message
    // written for them; the diagnostic detail goes to the console and the
    // `error` event.
    chrome.hint.textContent = error ? this.#errorHint : "";
    // Only the in-call states are worth announcing; echoing the idle label
    // would speak the button's own name back on every render.
    chrome.srStatus.textContent = active || connecting || error ? label : "";
  }

  // ── Text-chat mode (mode="text") ──────────────────────────────────────

  #isTextMode(): boolean {
    return (this.getAttribute("mode") ?? "voice").toLowerCase() === "text";
  }

  async #toggleText() {
    if (this.#textOpen) {
      this.#textOpen = false;
      this.#render();
      return;
    }
    this.#textOpen = true;
    this.#render();
    await this.#ensureTextStarted();
  }

  /**
   * Lazily creates the text conversation + controller on first open. The
   * transport (injected by the host) holds the credential and talks to the
   * text channel; a plan/quota refusal surfaces here as a panel notice rather
   * than an exception out of the click handler.
   */
  async #ensureTextStarted() {
    const chrome = this.#chrome;
    if (!chrome) return;
    const panel = chrome.textPanel;
    if (this.#textController) {
      panel.focusInput();
      return;
    }

    const transport = this.#textTransport;
    if (!transport) {
      panel.setNotice("Text chat isn't configured for this embed.");
      return;
    }

    // Honour a configured terms gate on the same authorization path the voice
    // toggle takes - a no-op when there is no agent-id / widget-config (the
    // console preview and most text embeds).
    this.#termsPending = true;
    this.#render();
    let authorized: CallSubject | null;
    try {
      authorized = await this.#authorizeCall();
    } finally {
      this.#termsPending = false;
      this.#render();
    }
    if (!authorized) {
      this.#textOpen = false;
      this.#render();
      return;
    }

    const controller = new TextChatController(transport, {
      onUserMessage: (text, timestamp) => {
        panel.appendMessage("user", text);
        this.dispatchEvent(new CustomEvent("message", { detail: { role: "user", text, timestamp } }));
      },
      onAssistantMessage: (text, timestamp) => {
        panel.appendMessage("assistant", text);
        this.dispatchEvent(new CustomEvent("message", { detail: { role: "assistant", text, timestamp } }));
      },
      onThinking: (thinking) => {
        panel.setThinking(thinking);
        // Don't re-enable a composer the terminal notice has already
        // hidden (a turn that both replied and ended).
        if (!this.#textController?.ended && !this.#textController?.unavailable) {
          panel.setInputEnabled(!thinking);
          if (!thinking) panel.focusInput();
        }
      },
      onEnded: () => {
        panel.setNotice("Conversation ended.");
        this.dispatchEvent(new CustomEvent("status", { detail: "ended" }));
      },
      onError: (err) => {
        this.#warn("<speechify-agent> text turn failed", err);
        panel.setInputEnabled(true);
        panel.focusInput();
      },
      onUnavailable: (reason) => {
        panel.setNotice(reason);
      },
    });
    this.#textController = controller;

    panel.setInputEnabled(false);
    if (await controller.ensureConversation()) {
      panel.setInputEnabled(true);
      panel.focusInput();
    }
  }

  /**
   * Render for mode="text": the launcher button + the chat panel, with every
   * voice-only surface suppressed. The chat panel owns its own open/close CSS,
   * driven by #textOpen.
   */
  #renderText(chrome: Chrome) {
    const appearance = this.#appearance;
    const { fab } = appearance;

    chrome.button.classList.toggle("fab", fab);
    chrome.button.classList.toggle("active", this.#textOpen);
    chrome.button.classList.remove("connecting");
    chrome.button.disabled = this.#termsPending;

    if (appearance.themeFromConfig) this.setAttribute("widget-theme", appearance.themeFromConfig);

    // Suppress every voice-only surface; text mode uses its own panel.
    chrome.controls.popup.classList.remove("open");
    chrome.transcript.hide();
    this.#paintOrb(chrome.controls.orb, chrome.controls.orbWrap, false);
    this.#paintOrb(chrome.orb, chrome.orbWrap, false);
    chrome.hint.textContent = "";
    chrome.srStatus.textContent = "";

    chrome.textPanel.setMode(fab ? "fab" : "pill");
    if (this.#textOpen) chrome.textPanel.show();
    else chrome.textPanel.hide();

    // The host needs relative positioning for the absolutely-positioned panel.
    if (this.#textOpen) this.style.position = "relative";
    else this.style.removeProperty("position");

    const label = this.getAttribute("start-call-text") ?? appearance.text.start ?? DEFAULT_TEXT_START_TEXT;
    chrome.label.textContent = fab ? "" : label;
    chrome.label.style.display = fab ? "none" : "";
    chrome.button.setAttribute("aria-label", label);
  }

  /**
   * Drives one orb's animation classes from the current status.
   *
   * `idleBreathing` is the button orb only: at rest it is static, so a page
   * carrying the widget is not animating forever, and the wrapper's halo
   * follows the same states as the orb itself. The panel orb has no wrapper
   * halo while connecting, which is the one asymmetry between the two.
   */
  #paintOrb(orb: HTMLElement, wrap: HTMLElement, idleBreathing: boolean): void {
    orb.classList.remove("speaking", "listening", "thinking", "connecting");
    wrap.classList.remove("speaking", "listening", "thinking");
    if (idleBreathing) {
      const active = this.#status === "listening" || this.#status === "thinking" || this.#status === "speaking";
      const connecting = this.#status === "connecting" || this.#status === "reconnecting";
      orb.classList.toggle("in-call", active || connecting);
    }
    switch (this.#status) {
      case "speaking":
      case "listening":
      case "thinking":
        orb.classList.add(this.#status);
        wrap.classList.add(this.#status);
        break;
      case "connecting":
        orb.classList.add("connecting");
        break;
      default:
        break;
    }
  }
}

export function registerCustomElement(): void {
  if (!customElements.get(TAG)) {
    customElements.define(TAG, SpeechifyAgentElement);
  }
}
